import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import {
  useResetPasswordWithOtpMutation,
  useVerifyOtpMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { routes } from "@/routes/utils";
import { getRequiredFields, removeEmptyFields } from "@/lib/utils";
import { schema } from "./validations";
import { CONFIRM_PASSWORD, NEW_PASSWORD, OTP } from "./consts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { setForgotPwdEmail } from "@/redux/slices";

const useResetPasswordWithOtp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showOtpSection, setShowOtpSection] = useState(true);
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const otpInputRef = useRef([]);
  const [isPassword, setIsPassword] = useState<any>({
    newPassword: true,
    confirm: true,
  });

  const form = useForm<any>({
    defaultValues: {
      [OTP]: "",
      [NEW_PASSWORD]: "",
      [CONFIRM_PASSWORD]: "",
    },
    resolver: zodResolver(schema),
  });
  
// Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  const { mutate: resetPasswordInfo, isPending: isRestPasswordPending } =
    useResetPasswordWithOtpMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        navigate(`/${routes.login}`);
        dispatch(setForgotPwdEmail(""));
        toast.success("Your password has been reset successfully.");
      },
    });

  const { mutate: validateOtpInfo, isPending: isVerifyOtpPending } =
    useVerifyOtpMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Invalid OTP"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setShowOtpSection(false);
      },
    });

  const forgotPwdEmail = useSelector(
    (state: RootState) => state.currentData.forgotPwdEmail
  );

  const onSubmit = (data: any) => {
    const _data = removeEmptyFields(data);

    if (!_data[NEW_PASSWORD]) {
      form.setError(NEW_PASSWORD, {
        message: "The minimum length required is 8 characters.",
      });
      return;
    }

    if (_data[NEW_PASSWORD] != _data[CONFIRM_PASSWORD]) {
      form.setError(CONFIRM_PASSWORD, {
        message: "New Password and Confirm Password must match.",
      });
      return;
    }

    delete _data[CONFIRM_PASSWORD];
    resetPasswordInfo({ data: { ..._data, email: forgotPwdEmail,
        [OTP]: form.watch(OTP),
      },
    });
  };

  const onEyeIconClick = (key: string) => () => {
    setIsPassword({ ...isPassword, [key]: !isPassword[key] });
  };

   const Icon = isPassword ? Eye : EyeOff;

  const onNextClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const enteredOtp = form.watch(OTP);

    if (!enteredOtp || enteredOtp?.length != 6) {
      toast.error("Please enter valid 6-digit OTP");
      return;
    }

    validateOtpInfo({
      data: {
        email: forgotPwdEmail,
        [OTP]: enteredOtp,
      },
    });
  };

  const onEnterOtp = (text: string, index: number) => {
    const normalizedText = text.replace(/\D/g, "").slice(0, 1);

    if (text && !normalizedText) return;

    const newOtp = [...otp];
    newOtp[index] = normalizedText;
    setOtp(newOtp);
    form.setValue(OTP, newOtp.join(""));

    if (normalizedText && index < otp.length - 1) {
      (otpInputRef.current[index + 1] as any)?.focus();
    } else if (!normalizedText && text === "" && index > 0) {
      (otpInputRef.current[index - 1] as any)?.focus();
    }
  };

  return {
    navigate,
    form,
    requiredFields,
    onSubmit,
    onEyeIconClick,
    isPassword,
    Icon,  
    isRestPasswordPending,
    isVerifyOtpPending,
    onNextClick,
    showOtpSection,
    onEnterOtp,
    otp,
    otpInputRef,
  };
};

export default useResetPasswordWithOtp;
